import { Component, ElementRef, OnDestroy, OnInit, booleanAttribute, computed, effect, inject, input, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MAT_DATE_RANGE_SELECTION_STRATEGY, MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { DataService } from '../../services/data.service';
import { SettingsService } from '../../services/settings.service';
import { ApiService } from '../../services/api.service';
import { DataFilterService } from '../../services/data-filter.service';
import { DataFilterComponent } from './data-filter/data-filter.component';
import { FixedEndDateRangeStrategy, startOfToday } from './fixed-end-range-strategy';
import { MatExpansionModule } from '@angular/material/expansion';
import { FilterCatalog } from '../../models/dataFilter/filterCatalog.model';

/** The quick date-range choices offered next to the picker. */
export type DateRangePreset = 'today' | 'last7' | 'last14' | 'last30';

/**
 * Each preset's length in days, counting today as day one — "Last 7 days" is
 * today plus the six before it, not the seven days before today.
 */
const PRESET_DAYS: Record<DateRangePreset, number> = {
  today: 1,
  last7: 7,
  last14: 14,
  last30: 30
};

/**
 * A preset as concrete dates: local midnight today back to the start of the
 * window, both ends inclusive. Local rather than UTC because the reports send
 * the calendar day, not an instant.
 *
 * Exported so anything showing a fixed window — a summary card, say — uses the
 * same definition of "last 7 days" as the picker does.
 */
export function rangeForPreset(preset: DateRangePreset): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - (PRESET_DAYS[preset] - 1));
  return { start, end };
}

/** Every preset button, in display order — each page shows a subset. */
export const DATE_RANGE_PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last14', label: 'Last 14 days' },
  { value: 'last30', label: 'Last 30 days' }
];

/**
 * Filter bar shown above the report content. Hosts the date range control and
 * its quick presets, an "Additional Filters" button that opens the data-scope
 * filter dialog, and a chip grid summarising the currently applied additional
 * filters. Selecting a complete date range reloads the scans.
 *
 * Shared by every report page; `defaultPreset` is how a page opens on a range
 * of its own.
 */
@Component({
  selector: 'app-filter-bar',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatInputModule,
    MatDatepickerModule,
    MatCardModule,
    MatChipsModule,
    MatExpansionModule
],
  // Two entries for one instance: the bar injects the class to flip its flag,
  // the picker resolves the same object through the token.
  providers: [
    FixedEndDateRangeStrategy,
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useExisting: FixedEndDateRangeStrategy }
  ],
  templateUrl: './filter-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './filter-bar.component.scss'
})
export class FilterBarComponent implements OnInit, OnDestroy {
  private readonly data = inject(DataService);
  private readonly settings = inject(SettingsService);
  private readonly api = inject(ApiService);
  private readonly adapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly dateFormats = inject(MAT_DATE_FORMATS);
  private readonly dialog = inject(MatDialog);
  private readonly rangeStrategy = inject(FixedEndDateRangeStrategy);
  private readonly snackBar = inject(MatSnackBar);

  EMPTY_CATALOG: FilterCatalog = {
    labels: {
      businessTypes: 'Business Type',
      processors: 'Processor',
      countries: 'Country',
      states: 'State',
      sites: 'Site'
    },
    businessTypes: [], processors: [], countries: [], states: [], sites: []
  };

  /** Applied data filters and their chips — owned by the service, rendered here. */
  readonly filters = inject(DataFilterService);

  /** The range this page opens on. Reports that want their own override it. */
  readonly defaultPreset = input<DateRangePreset>('last7');

  /**
   * Pins the end of the range to the current date: the end input goes read-only
   * and the calendar only picks a start. For the reports that only ever look
   * backwards from today.
   */
  readonly fixedEndDate = input(false, { transform: booleanAttribute });

  /** Leaves out the Data Filters button and applied-filter chips, for pages the data filters don't scope. */
  readonly hideDataFilters = input(false, { transform: booleanAttribute });

  /** Longest range the calendar allows, in months. Unset, ranges are unbounded. */
  readonly maxRangeMonths = input<number | null>(null);

  /** Which preset buttons this page offers. Display order comes from DATE_RANGE_PRESETS. */
  readonly presets = input<DateRangePreset[]>(['today', 'last7', 'last14']);

  /**
   * The picker's upper bound on every page: nothing has data after today.
   * Refreshed on every open, so a tab left open across midnight doesn't cap
   * selection at yesterday.
   *
   * Same-day writes compare equal, so re-reading the clock on a picker opened
   * twice in one day notifies nothing.
   */
  private readonly today = signal(startOfToday(), {
    equal: (a, b) => a.getTime() === b.getTime()
  });

  readonly maxDate = this.today.asReadonly();

  /** Apply is only meaningful once the pending selection has both ends. */
  readonly applyDisabled = computed(() => !this.rangeStrategy.complete());

  readonly presetOptions = computed(() =>
    DATE_RANGE_PRESETS.filter(option => this.presets().includes(option.value)));

  /** Which preset the current range matches, or null when it matches none. */
  readonly preset = signal<DateRangePreset | null>(null);

  /** Selected date range driving the scan query. */
  readonly range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });

  private readonly rangeInput = viewChild(MatDateRangeInput);
  private readonly startEl = viewChild<ElementRef<HTMLInputElement>>('startEl');
  private readonly endEl = viewChild<ElementRef<HTMLInputElement>>('endEl');

  constructor() {
    // The picker reaches the strategy through the injection token, not through
    // this component, so the input has to be mirrored onto the instance.
    effect(() => this.rangeStrategy.locked = this.fixedEndDate());
    effect(() => this.rangeStrategy.maxRangeMonths = this.maxRangeMonths());

    // The strategy corrects some selections on its own; say so, or the change
    // looks like the calendar ignored the click.
    this.rangeStrategy.corrections
      .pipe(takeUntilDestroyed())
      .subscribe(message => this.snackBar.open(message, 'Dismiss', { duration: 5000 }));

    // Repaint the range text when the locale changes. The adapter already
    // formats with the current locale (PortalDateAdapter), but Material only
    // writes the input text when the value changes — and forcing that through
    // the form control would re-emit valueChanges, which is the coupling this
    // whole arrangement exists to avoid.
    effect(() => {
      this.settings.locale();
      const startEl = this.startEl();
      const endEl = this.endEl();
      if (!startEl || !endEl) {
        return;
      }
      const { start, end } = this.range.getRawValue();
      startEl.nativeElement.value = this.formatForInput(start);
      endEl.nativeElement.value = this.formatForInput(end);
      // The range input sizes each field from a hidden mirror span and is
      // OnPush, so it has to be told the text was reformatted.
      this.rangeInput()?._handleChildValueChange();
    });

    // Reload whenever a complete range is selected (calendar Apply or a preset).
    // Material re-emits valueChanges for reasons that aren't user edits — a
    // reformat, or the value accessor being wired up on mount — so compare
    // against the stored range before kicking off another load.
    this.range.valueChanges.subscribe(({ start, end }) => {
      if (!start || !end || this.isCurrentRange(start, end)) {
        return;
      }
      // Keep the toggle in step with the range however it was chosen, so a
      // range picked in the calendar that happens to be one of the presets
      // still lights it up, and anything else clears the selection.
      this.preset.set(this.matchPreset(start, end));
      /* this.api.loadScans(start, end); */
      this.data.setDateRange({ start, end })
    });
  }

  ngOnInit(): void {
    // Applied here rather than in the constructor: signal inputs are not bound
    // yet at construction, so defaultPreset() would always read 'last7'.
    this.applyPreset(this.defaultPreset());
  }

  ngOnDestroy(): void {
    // Each report opens on its own default preset, so the range must not
    // outlive the page. Left in place, the next report's component replays it
    // on mount and loads it before this bar applies that page's default —
    // two requests for two different ranges, racing to render.
    this.data.setDateRange(null);
  }


  /** A picker date as input text in the current locale, or '' when unset. */
  private formatForInput(date: Date | null): string {
    return date && this.adapter.isValid(date)
      ? this.adapter.format(date, this.dateFormats.display.dateInput)
      : '';
  }

  /** Whether these dates are the range already loaded (same day, not same object). */
  private isCurrentRange(start: Date, end: Date): boolean {
    const current = this.data.dateRange();
    return current?.start.getTime() === start.getTime()
      && current?.end.getTime() === end.getTime();
  }

  /** Called when the calendar opens. */
  onPickerOpened(): void {
    // Re-read the current date so a tab left open across midnight doesn't cap
    // selection at yesterday.
    this.today.set(startOfToday());

    // The calendar opens on the committed range, so Apply starts out matching
    // whatever that range is — complete, in every normal case.
    const { start, end } = this.range.getRawValue();
    this.rangeStrategy.complete.set(start != null && end != null);
  }

  /** Selecting a preset button. */
  applyPreset(preset: DateRangePreset): void {
    // Set before the form so the toggle stays lit even when the range is
    // already this one and valueChanges short-circuits.
    this.preset.set(preset);
    this.range.setValue(this.rangeFor(preset));
  }

  /** A preset as concrete dates — see rangeForPreset. */
  private rangeFor(preset: DateRangePreset): { start: Date; end: Date } {
    return rangeForPreset(preset);
  }

  /** The shown preset these dates correspond to, or null if they match none. */
  private matchPreset(start: Date, end: Date): DateRangePreset | null {
    return this.presetOptions().find(({ value }) => {
      const candidate = this.rangeFor(value);
      return candidate.start.getTime() === start.getTime()
        && candidate.end.getTime() === end.getTime();
    })?.value ?? null;
  }

  /** Open the data-scope filters in a dialog. */
  openAdditionalFilters(): void {
    this.dialog.open(DataFilterComponent, { width: '680px', maxWidth: '95vw', autoFocus: false });
  }
}
